import { Button, IconButton, VisuallyHidden } from '@artosera/ui';
import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import {
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { deplacer, type IdModule } from './ordreModules.js';
import styles from './BlocsReorganisables.module.css';

export interface BlocAffiche {
  readonly id: IdModule;
  readonly libelle: string;
  readonly contenu: ReactNode;
}

interface Props {
  /** Les blocs à afficher, déjà dans l'ordre courant. */
  readonly blocs: readonly BlocAffiche[];
  readonly edition: boolean;
  /** Appelé à chaque déplacement abouti, avec le nouvel ordre des blocs affichés. */
  readonly onOrdre: (ordre: IdModule[]) => void;
  readonly onReinitialiser: () => void;
  readonly onTermine: () => void;
}

type Controle = 'poignee' | 'monter' | 'descendre';

interface Glisse {
  readonly id: IdModule;
  /** Décalage vertical du bloc saisi par rapport à sa place. */
  readonly dy: number;
  /** Position visée parmi les AUTRES blocs (0 = en tête). */
  readonly cible: number;
}

const MARGE_DEFILEMENT = 72;

/**
 * Les blocs du tableau de bord, réordonnables à la demande.
 *
 * Hors édition, chaque bloc est un enfant direct de la page, exactement comme
 * avant (les thèmes ciblent ces enfants). En édition, chaque bloc est enveloppé
 * d'une barre de commandes — poignée (glisser à la souris ou au doigt, flèches
 * du clavier), monter, descendre — et son contenu devient inerte : un geste
 * malheureux n'ouvre pas une fiche en passant. Chaque déplacement est annoncé
 * dans une zone `aria-live`.
 */
export function BlocsReorganisables({
  blocs,
  edition,
  onOrdre,
  onReinitialiser,
  onTermine,
}: Props): JSX.Element {
  const { t } = useTranslation('regie');
  const idAide = useId();
  const racines = useRef(new Map<IdModule, HTMLElement>());
  const [glisse, setGlisse] = useState<Glisse | null>(null);
  const [annonce, setAnnonce] = useState('');
  const aRefocaliser = useRef<{ id: IdModule; controle: Controle } | null>(null);
  const arreter = useRef<(() => void) | null>(null);

  // Dernières valeurs, lues par les écouteurs posés sur `window` pendant un glisser.
  const dernier = useRef({ blocs, onOrdre, t });
  useEffect(() => {
    dernier.current = { blocs, onOrdre, t };
  });

  const ids = blocs.map((bloc) => bloc.id);
  const libelleDe = (id: IdModule): string => blocs.find((bloc) => bloc.id === id)?.libelle ?? id;

  // Après un déplacement au clavier, le nœud a pu être déplacé dans le DOM : le focus suit.
  useLayoutEffect(() => {
    const cible = aRefocaliser.current;
    if (!cible) return;
    aRefocaliser.current = null;
    racines.current
      .get(cible.id)
      ?.querySelector<HTMLElement>(`[data-controle="${cible.controle}"]`)
      ?.focus();
  });

  // Sortie du mode édition ou démontage : plus de glisser en cours.
  useEffect(() => {
    if (!edition) arreter.current?.();
    return () => arreter.current?.();
  }, [edition]);

  const deplacerDe = (id: IdModule, pas: -1 | 1, controle: Controle): void => {
    const index = ids.indexOf(id);
    const vers = index + pas;
    aRefocaliser.current = { id, controle };
    if (vers < 0 || vers >= ids.length) {
      setAnnonce(
        t(
          pas < 0
            ? 'tableauDeBord.personnaliser.dejaPremier'
            : 'tableauDeBord.personnaliser.dejaDernier',
          {
            nom: libelleDe(id),
          },
        ),
      );
      return;
    }
    onOrdre(deplacer(ids, index, vers));
    setAnnonce(
      t('tableauDeBord.personnaliser.annonceDeplace', {
        nom: libelleDe(id),
        position: vers + 1,
        total: ids.length,
      }),
    );
  };

  const surTouche = (id: IdModule, e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      deplacerDe(id, e.key === 'ArrowUp' ? -1 : 1, 'poignee');
    }
  };

  const saisir = (id: IdModule, e: ReactPointerEvent<HTMLButtonElement>): void => {
    if (e.button !== 0 || arreter.current) return;
    e.preventDefault();
    const { blocs: courants, t: tr } = dernier.current;
    const depart = {
      y: e.clientY,
      defilement: window.scrollY,
    };
    let pointeurY = e.clientY;
    let image = 0;
    const derniereCible = courants.findIndex((bloc) => bloc.id === id);
    let courant: Glisse = { id, dy: 0, cible: derniereCible };

    const calculer = (): void => {
      const { blocs: liste } = dernier.current;
      const autres = liste.filter((bloc) => bloc.id !== id);
      let cible = 0;
      for (const autre of autres) {
        const rect = racines.current.get(autre.id)?.getBoundingClientRect();
        if (rect && rect.top + rect.height / 2 < pointeurY) cible += 1;
      }
      const dy = pointeurY - depart.y + (window.scrollY - depart.defilement);
      if (dy !== courant.dy || cible !== courant.cible) {
        courant = { id, dy, cible };
        setGlisse(courant);
      }
    };

    const boucle = (): void => {
      // Défilement automatique près des bords : le doigt reste immobile mais la page glisse.
      if (pointeurY < MARGE_DEFILEMENT) window.scrollBy(0, -12);
      else if (pointeurY > window.innerHeight - MARGE_DEFILEMENT) window.scrollBy(0, 12);
      calculer();
      image = requestAnimationFrame(boucle);
    };

    const surMouvement = (ev: PointerEvent): void => {
      pointeurY = ev.clientY;
    };
    const nettoyer = (): void => {
      cancelAnimationFrame(image);
      window.removeEventListener('pointermove', surMouvement);
      window.removeEventListener('pointerup', surRelache);
      window.removeEventListener('pointercancel', surAnnule);
      window.removeEventListener('keydown', surEchap);
      arreter.current = null;
      setGlisse(null);
    };
    const surRelache = (): void => {
      calculer();
      const { blocs: liste, onOrdre: valider, t: trad } = dernier.current;
      const index = liste.findIndex((bloc) => bloc.id === id);
      nettoyer();
      if (courant.cible === index) return;
      valider(
        deplacer(
          liste.map((bloc) => bloc.id),
          index,
          courant.cible,
        ),
      );
      setAnnonce(
        trad('tableauDeBord.personnaliser.annonceDeplace', {
          nom: liste[index]!.libelle,
          position: courant.cible + 1,
          total: liste.length,
        }),
      );
    };
    const surAnnule = (): void => {
      nettoyer();
      setAnnonce(dernier.current.t('tableauDeBord.personnaliser.annonceAnnule'));
    };
    const surEchap = (ev: globalThis.KeyboardEvent): void => {
      if (ev.key === 'Escape') surAnnule();
    };

    arreter.current = nettoyer;
    window.addEventListener('pointermove', surMouvement);
    window.addEventListener('pointerup', surRelache);
    window.addEventListener('pointercancel', surAnnule);
    window.addEventListener('keydown', surEchap);
    image = requestAnimationFrame(boucle);
    setGlisse(courant);
    setAnnonce(
      tr('tableauDeBord.personnaliser.annonceSaisi', {
        nom: libelleDe(id),
        position: derniereCible + 1,
        total: courants.length,
      }),
    );
  };

  // Le repère d'insertion : un filet au-dessus (ou au-dessous du dernier) du bloc visé.
  const reperes = new Map<IdModule, 'avant' | 'apres'>();
  if (glisse) {
    const autres = ids.filter((id) => id !== glisse.id);
    if (glisse.cible !== ids.indexOf(glisse.id)) {
      if (glisse.cible < autres.length) reperes.set(autres[glisse.cible]!, 'avant');
      else if (autres.length > 0) reperes.set(autres[autres.length - 1]!, 'apres');
    }
  }

  return (
    <>
      {edition ? (
        <section className={styles.barre} aria-label={t('tableauDeBord.personnaliser.titre')}>
          <p id={idAide} className={styles.aide}>
            {t('tableauDeBord.personnaliser.aide')}
          </p>
          <div className={styles.barreActions}>
            <Button variant="ghost" size="sm" onClick={onReinitialiser}>
              {t('tableauDeBord.personnaliser.reinitialiser')}
            </Button>
            <Button variant="primary" size="sm" onClick={onTermine}>
              {t('tableauDeBord.personnaliser.termine')}
            </Button>
          </div>
        </section>
      ) : null}

      {blocs.map((bloc, index) => {
        if (!edition) return <Fragment key={bloc.id}>{bloc.contenu}</Fragment>;
        const saisi = glisse?.id === bloc.id;
        const repere = reperes.get(bloc.id);
        return (
          <div
            key={bloc.id}
            ref={(noeud) => {
              if (noeud) racines.current.set(bloc.id, noeud);
              else racines.current.delete(bloc.id);
            }}
            className={[
              styles.bloc,
              saisi ? styles.saisi : '',
              repere === 'avant' ? styles.repereAvant : '',
              repere === 'apres' ? styles.repereApres : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={saisi ? { transform: `translateY(${String(glisse.dy)}px)` } : undefined}
            data-bloc={bloc.id}
          >
            <div className={styles.commandes}>
              <IconButton
                size="sm"
                className={styles.poignee}
                data-controle="poignee"
                aria-label={t('tableauDeBord.personnaliser.deplacer', { nom: bloc.libelle })}
                aria-describedby={idAide}
                icon={<GripVertical size={16} strokeWidth={1.5} aria-hidden="true" />}
                onPointerDown={(e) => saisir(bloc.id, e)}
                onKeyDown={(e) => surTouche(bloc.id, e)}
              />
              <span className={styles.nom}>{bloc.libelle}</span>
              <span className={styles.rang} aria-hidden="true">
                {index + 1} / {blocs.length}
              </span>
              <IconButton
                variant="outline"
                size="sm"
                data-controle="monter"
                aria-label={t('tableauDeBord.personnaliser.monter', { nom: bloc.libelle })}
                aria-disabled={index === 0 || undefined}
                icon={<ChevronUp size={16} strokeWidth={1.5} aria-hidden="true" />}
                onClick={() => deplacerDe(bloc.id, -1, 'monter')}
              />
              <IconButton
                variant="outline"
                size="sm"
                data-controle="descendre"
                aria-label={t('tableauDeBord.personnaliser.descendre', { nom: bloc.libelle })}
                aria-disabled={index === blocs.length - 1 || undefined}
                icon={<ChevronDown size={16} strokeWidth={1.5} aria-hidden="true" />}
                onClick={() => deplacerDe(bloc.id, 1, 'descendre')}
              />
            </div>
            <div className={styles.contenu} inert>
              {bloc.contenu}
            </div>
          </div>
        );
      })}

      <VisuallyHidden>
        <p role="status" aria-live="polite" aria-atomic="true">
          {annonce}
        </p>
      </VisuallyHidden>
    </>
  );
}
